import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Database, ChevronRight, Trash2, TrendingUp, TrendingDown, AlertTriangle, Sparkles, Inbox } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";
import { playChime } from "../../lib/chime";
import { useContextualAudio } from "../../lib/useContextualAudio";

const STATUS_LABEL = {
  pending: "Pendente", confirmed: "Confirmado", in_progress: "Em curso",
  completed: "Concluído", cancelled: "Cancelado",
};

export default function AdminDashboard() {
  useContextualAudio("ocean");
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [todayBookings, setTodayBookings] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [contactsUnread, setContactsUnread] = useState(0);
  const nav = useNavigate();

  const load = () => {
    api.get("/admin/dashboard").then(({ data }) => setStats(data));
    api.get("/admin/allocation-alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/admin/contact-requests")
      .then(({ data }) => setContactsUnread(data.unread || 0))
      .catch(() => {});
    const today = todayLocalIso();
    api.get("/bookings", { params: { date: today } })
      .then(({ data }) => setTodayBookings((data.items || []).filter(b => b.scheduled_date === today)))
      .catch(() => {});
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  function go(path) {
    playChime();
    nav(path);
  }

  async function seedDemo() {
    if (seeding) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/dev/seed-demo");
      playChime({ volume: 0.25 });
      toast.success(`Dados demo criados: ${data.summary}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  }

  async function clearDemo() {
    if (cleaning) return;
    if (!window.confirm("Remover TODOS os dados demo (clientes/funcionários/apartamentos/reservas/faturas/problemas/extras criados pelos testes)? Os seus dados reais permanecem intactos.")) return;
    setCleaning(true);
    try {
      const { data } = await api.delete("/admin/dev/clear-demo");
      const d = data.deleted;
      toast.success(
        `Limpeza concluída: ${d.users} utilizadores · ${d.apartments} apartamentos · ${d.bookings} reservas · ${d.invoices} faturas · ${d.issue_reports} problemas · ${d.extra_suggestions} extras removidos`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  }

  async function resetAll() {
    if (cleaning) return;
    const typed = window.prompt(
      "⚠️ RESET TOTAL IRREVERSÍVEL\n\n" +
      "Isto irá apagar TUDO: utilizadores, apartamentos, reservas, faturas, extras, problemas, despesas, promoções.\n" +
      "APENAS a sua conta de administrador ficará.\n\n" +
      "Para confirmar, escreva exactamente: APAGAR TUDO"
    );
    if (typed !== "APAGAR TUDO") {
      if (typed !== null) toast.error("Reset cancelado — texto de confirmação não corresponde.");
      return;
    }
    setCleaning(true);
    try {
      const { data } = await api.delete("/admin/dev/reset-all");
      const d = data.deleted;
      const total = Object.values(d).reduce((a, b) => a + b, 0);
      toast.success(`Reset total concluído: ${total} registos apagados. Apenas ${data.preserved_admin} permanece.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  }

  if (!stats) return <div className="text-[#a0b2af]" data-testid="admin-dash-loading">A carregar…</div>;

  return (
    <div className="space-y-10">
      {contactsUnread > 0 && (
        <button onClick={() => go("/admin/emails")}
                className="w-full text-left p-4 lux-card border-[#d4af37] bg-gradient-to-r from-[#d4af37]/10 to-transparent flex items-center gap-4 hover:bg-[#d4af37]/15 transition-colors animate-pulse-slow"
                data-testid="dash-contacts-banner">
          <div className="w-12 h-12 rounded-sm bg-[#d4af37]/20 border border-[#d4af37]/50 flex items-center justify-center text-[#d4af37] shrink-0">
            <Inbox size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="eyebrow text-[#d4af37] mb-0.5">Pedidos de contacto</div>
            <div className="text-base text-[#fdfbf7]">
              <strong className="text-[#d4af37]">{contactsUnread}</strong>{" "}
              {contactsUnread === 1 ? "pedido novo aguarda resposta" : "pedidos novos aguardam resposta"}
            </div>
          </div>
          <ChevronRight size={20} className="text-[#d4af37] shrink-0" />
        </button>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-3">Visão geral</div>
          <h1 className="font-display text-5xl">Dashboard.</h1>
          <div className="text-[#a0b2af] text-xs mt-2 uppercase tracking-widest">
            Atualização automática · Clique em qualquer indicador para abrir a área correspondente
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={seedDemo} disabled={seeding}
                  className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="seed-demo-btn"
                  title="Gera clientes, funcionários, apartamentos, reservas e faturas para testar">
            <Database size={14} />
            {seeding ? "A gerar..." : "Gerar dados demo"}
          </button>
          <button onClick={clearDemo} disabled={cleaning}
                  className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 !border-[#ef4444]/40 hover:!border-[#ef4444] text-[#fca5a5]"
                  data-testid="clear-demo-btn"
                  title="Remove apenas dados demo/teste. Mantém admin + contas reais.">
            <Trash2 size={14} />
            {cleaning ? "A limpar..." : "Limpar dados demo"}
          </button>
          <button onClick={resetAll} disabled={cleaning}
                  className="px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 border border-[#ef4444] bg-[#ef4444]/10 text-[#fca5a5] hover:bg-[#ef4444]/20 transition-colors rounded-sm"
                  data-testid="reset-all-btn"
                  title="⚠️ Reset total: apaga TUDO excepto admin. Irreversível.">
            <AlertTriangle size={14} />
            {cleaning ? "…" : "Reset total"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Stat testId="stat-today" label="Limpezas hoje" value={stats.today_bookings} primary
              onClick={() => go("/admin/reservas")} />
        <Stat testId="stat-completed-today" label="Concluídas hoje" value={stats.completed_today}
              onClick={() => go("/admin/reservas")} />
        <Stat testId="stat-pending-bookings" label="Reservas pendentes" value={stats.pending_bookings} accent
              onClick={() => go("/admin/reservas")} />
        <Stat testId="stat-pending-invoices" label="Faturas rascunho" value={stats.pending_invoices} accent
              onClick={() => go("/admin/faturas")} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Stat testId="stat-clients" label="Clientes" value={stats.total_clients}
              onClick={() => go("/admin/utilizadores")} />
        <Stat testId="stat-employees" label="Funcionários ativos" value={stats.total_employees}
              onClick={() => go("/admin/utilizadores")} />
        <Stat testId="stat-pending-employees" label="Funcionários pendentes" value={stats.pending_employees} accent
              onClick={() => go("/admin/utilizadores")} />
        <Stat testId="stat-revenue" label="Receita do mês" value={`${stats.monthly_revenue.toFixed(2)}€`} primary
              onClick={() => go("/admin/faturas")} />
      </div>

      <CostPerCleaningCard stats={stats} onClick={() => go("/admin/produtos-gastos")} />

      {todayBookings.length > 0 && (
        <section className="lux-card p-5 md:p-6 ring-2 ring-[#d4af37] bg-[#d4af37]/5 shadow-[0_0_24px_rgba(212,175,55,0.15)]"
                 data-testid="admin-today-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-widest text-[#041f1b] bg-[#d4af37] font-semibold rounded-sm">
                Hoje
              </div>
              <h2 className="font-display text-xl md:text-2xl">Limpezas agendadas para hoje</h2>
            </div>
            <button onClick={() => go("/admin/reservas")}
                    className="text-xs text-[#d4af37] uppercase tracking-widest inline-flex items-center gap-1 hover:underline"
                    data-testid="admin-today-view-all">
              Ver todas <ChevronRight size={12} />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {todayBookings.slice(0, 6).map(b => (
              <button key={b.id} onClick={() => nav("/admin/reservas")}
                      className="text-left p-3 border border-[#1a4640] rounded-sm hover:border-[#d4af37] bg-[#062c27]/40 transition-colors flex items-center gap-3"
                      data-testid={`admin-today-${b.id}`}>
                <div className="font-mono text-lg text-[#d4af37] w-14 shrink-0">{b.scheduled_time}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{b.apartment_name || b.apt_type} · {b.client_name}</div>
                  <div className="text-[10px] text-[#a0b2af] uppercase tracking-widest">
                    {b.apt_type} · {b.total.toFixed(2)}€ · {STATUS_LABEL[b.status] || b.status}
                    {b.assigned_employee_name && ` · ${b.assigned_employee_name}`}
                  </div>
                </div>
              </button>
            ))}
          </div>
          {todayBookings.length > 6 && (
            <div className="text-[11px] text-[#a0b2af] mt-3 text-center">
              + {todayBookings.length - 6} outras limpezas hoje
            </div>
          )}
        </section>
      )}

      {alerts?.recommendation && (
        <div className="lux-card p-5 border-l-4 border-[#d4af37] flex items-start gap-4" data-testid="allocation-alert">
          <TrendingUp className="text-[#d4af37] mt-1 flex-shrink-0" size={18} />
          <div className="flex-1">
            <div className="eyebrow text-[#d4af37] mb-1">Alocação inteligente</div>
            <div className="text-sm text-[#fdfbf7]">{alerts.recommendation}</div>
            {alerts.pressure_days_count > 0 && (
              <div className="text-[11px] text-[#a0b2af] mt-2">
                {alerts.pressure_days_count} dia{alerts.pressure_days_count > 1 ? "s" : ""} de pressão nas próximas 3 semanas ·{" "}
                <button onClick={() => go("/admin/capacidade")} className="text-[#d4af37] hover:underline">
                  Abrir gestor de capacidade →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, primary, testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group text-left p-6 border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(212,175,55,0.35)] active:translate-y-0 ${
        primary
          ? "border-[#d4af37] bg-[#0a3a33] hover:bg-[#0c4640]"
          : accent
          ? "border-[#d4af37]/40 hover:border-[#d4af37]"
          : "border-[#1a4640] hover:border-[#d4af37]/60"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-[#a0b2af] group-hover:text-[#d4af37] transition-colors">{label}</div>
        <ChevronRight size={14} className="text-[#1a4640] group-hover:text-[#d4af37] transition-colors" />
      </div>
      <div className={`font-display text-4xl mt-3 ${primary ? "text-[#d4af37]" : ""}`}>{value}</div>
    </button>
  );
}


function CostPerCleaningCard({ stats, onClick }) {
  const v = Number(stats.cost_per_cleaning || 0);
  const prev = Number(stats.cost_per_cleaning_prev || 0);
  const delta = stats.cost_per_cleaning_delta_pct;
  const up = typeof delta === "number" && delta > 0;
  const down = typeof delta === "number" && delta < 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="stat-cost-per-cleaning"
      className="w-full group text-left p-5 border border-[#1a4640] hover:border-[#d4af37]/60 transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(212,175,55,0.35)]"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 border border-[#d4af37]/40 text-[#d4af37]">
            <Sparkles size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[#a0b2af] group-hover:text-[#d4af37] transition-colors">
              Custo por limpeza este mês
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-display text-3xl text-[#fdfbf7]" data-testid="cost-per-cleaning-value">
                {v > 0 ? `${v.toFixed(2)}€` : "—"}
              </div>
              {delta !== null && typeof delta === "number" && (
                <div className={`inline-flex items-center gap-1 text-xs font-medium ${up ? "text-[#fca5a5]" : down ? "text-[#10b981]" : "text-[#a0b2af]"}`}
                     data-testid="cost-per-cleaning-delta">
                  {up ? <TrendingUp size={12} /> : down ? <TrendingDown size={12} /> : null}
                  {up ? "+" : ""}{delta.toFixed(1)}% vs mês anterior
                </div>
              )}
            </div>
            <div className="text-[11px] text-[#a0b2af] mt-1">
              {prev > 0 ? `Mês anterior: ${prev.toFixed(2)}€` : "Sem dados do mês anterior"}
            </div>
          </div>
        </div>
        <ChevronRight size={14} className="text-[#1a4640] group-hover:text-[#d4af37] transition-colors" />
      </div>
    </button>
  );
}
